import styled, { css } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { borderRadius } from "../../../design-system/tokens/borders";
import { FilterChip } from "../StoryFilterBar/StoryFilterBar.styles";
import {
  fontFamilies,
  fontSizes,
  fontWeights,
  lineHeights,
  typographyMixin,
} from "../../../design-system/tokens/typography";

/**
 * Story-title text style per Figma (24px / medium / 38px). The typography
 * scale has no 24px heading variant, so it is composed from token primitives.
 * Exported so the presentation deck title can share the exact same style.
 */
export const storyTitleTypography = css`
  font-family: ${fontFamilies.sans};
  font-size: ${fontSizes["2xl"]};
  font-weight: ${fontWeights.medium};
  line-height: ${lineHeights["38"]};
`;

/* Brand gradient 1px border via the same padding-box / border-box recipe
   as NarrativeWidget / PromptInputBar. */
export const HeaderCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing[3]};
  width: 100%;
  box-sizing: border-box;
  

`;

export const Title = styled.h1<{ $editable?: boolean }>`
  margin: 0;
  min-width: 0;
  ${storyTitleTypography}
  color: ${rawColors.text.highEmphasis};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $editable }) =>
    $editable &&
    css`
      cursor: text;
      border-radius: ${borderRadius.sm};
      &:hover {
        background: ${rawColors.grey[50]};
        box-shadow: 0 0 0 ${spacing[1]} ${rawColors.grey[50]};
      }
    `}
`;

/* Title + pen icon row; min-width 0 lets the title truncate inside it. */
export const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  min-width: 0;
  flex: 1;

  /* Pen stays subtle until the user hovers the title row. */
  & > button {
    color: ${rawColors.text.lowEmphasis};
    opacity: 0.6;
    flex-shrink: 0;
  }
  &:hover > button {
    opacity: 1;
  }
`;

/* Same box as Title so the layout doesn't shift when editing starts. */
export const TitleInput = styled.input`
  margin: 0;
  padding: 0;
  min-width: 0;
  flex: 1;
  ${storyTitleTypography}
  color: ${rawColors.text.highEmphasis};
  background: transparent;
  border: none;
  border-bottom: 1px solid ${rawColors.stroke[300]};
  outline: none;
  &:focus {
    border-bottom-color: ${rawColors.primary[500]};
  }
`;

export const Meta = styled.span`
  flex-shrink: 0;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.mediumEmphasis};
  white-space: nowrap;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[4]};
  min-width: 0;
`;

/**
 * Read-only drill-through context chip (screenshot: "↦ Drill through: Region
 * in west"). Reuses the filter chip's pill; it has no hover state because it
 * is not interactive — the back link is how the analyst leaves the context.
 */
export const DrillThroughChip = styled(FilterChip)`
  align-self: flex-start;
  max-width: 100%;
  gap: ${spacing[2]};

  &:hover {
    border-color: ${rawColors.primary[200]};
  }
`;

export const DrillThroughChipText = styled.span`
  min-width: 0;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.highEmphasis};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DrillThroughChipPrefix = styled.span`
  color: ${rawColors.text.mediumEmphasis};
`;
